import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const Process: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const stepVariants = {
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  const valueCardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  const processSteps = [
    {
      icon: "📋",
      title: "Business Requirements Analysis",
      description: "We dive deep into understanding your business goals, target audience, and specific needs to create a comprehensive project roadmap.",
      benefits: ["Clear project scope", "Reduced scope creep", "Better resource allocation"],
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: "📄",
      title: "Comprehensive Documentation",
      description: "Every requirement is meticulously documented, creating a clear blueprint that serves as our project bible throughout development.",
      benefits: ["Transparent communication", "Clear deliverables", "Easy project tracking"],
      color: "from-green-500 to-emerald-500"
    },
    {
      icon: "💻",
      title: "GitHub Project Setup",
      description: "Professional version control setup ensures code safety, collaboration, and maintainability for years to come.",
      benefits: ["Code version control", "Team collaboration", "Easy maintenance"],
      color: "from-purple-500 to-violet-500"
    },
    {
      icon: "🧩",
      title: "Modular Development Approach",
      description: "Breaking your project into manageable chunks ensures quality, faster delivery, and easier testing at every stage.",
      benefits: ["Faster development", "Better quality control", "Easier testing"],
      color: "from-orange-500 to-red-500"
    },
    {
      icon: "⏰",
      title: "Sprint-Based Delivery",
      description: "Agile methodology with regular sprints keeps you informed and ensures steady progress toward your goals.",
      benefits: ["Regular progress updates", "Flexible adjustments", "Predictable delivery"],
      color: "from-indigo-500 to-blue-500"
    },
    {
      icon: "💬",
      title: "Continuous Feedback Loop",
      description: "Regular check-ins and feedback sessions ensure your vision is perfectly aligned with the final product.",
      benefits: ["Client satisfaction", "Reduced revisions", "Perfect alignment"],
      color: "from-pink-500 to-rose-500"
    }
  ];

  const valuePropositions = [
    {
      icon: "💰",
      title: "Transparent Pricing",
      description: "No hidden costs or surprise fees. You know exactly what you're paying for and why.",
      color: "bg-green-100 text-green-600"
    },
    {
      icon: "🛡️",
      title: "Quality Assurance",
      description: "Every line of code is tested and reviewed to ensure your project meets the highest standards.",
      color: "bg-blue-100 text-blue-600"
    },
    {
      icon: "🚀",
      title: "Future-Proof Solutions",
      description: "We build with scalability and maintainability in mind, ensuring your investment grows with your business.",
      color: "bg-purple-100 text-purple-600"
    },
    {
      icon: "👥",
      title: "Dedicated Support",
      description: "Ongoing support and maintenance to ensure your project continues to perform optimally.",
      color: "bg-orange-100 text-orange-600"
    }
  ];

  return (
    <section id="process" className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="container mx-auto px-5">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-indigo-500 to-purple-600 bg-clip-text text-transparent">
            Our Proven Development Process
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-4">
            We follow a systematic approach that ensures quality, transparency, and value at every step. 
            Here's why our methodology justifies our pricing and delivers exceptional results.
          </p>
          <motion.button
            onClick={() => window.location.href = '/process'}
            className="text-indigo-600 hover:text-indigo-700 font-semibold underline underline-offset-4 transition-colors duration-300"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Learn more about our detailed process →
          </motion.button>
        </motion.div>

        {/* Process Steps */}
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="mb-20"
        >
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {processSteps.map((step, index) => (
              <motion.div
                key={index}
                variants={stepVariants}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 group"
                whileHover={{ y: -5 }}
              >
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-r ${step.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <span className="text-2xl">{step.icon}</span>
                </div>
                
                <h3 className="text-xl font-semibold text-gray-800 mb-4">
                  {step.title}
                </h3>
                
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {step.description}
                </p>
                
                <div className="space-y-2">
                  <h4 className="font-semibold text-gray-700 text-sm">Key Benefits:</h4>
                  <ul className="space-y-1">
                    {step.benefits.map((benefit, benefitIndex) => (
                      <li key={benefitIndex} className="flex items-center gap-2 text-sm text-gray-600">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Value Proposition */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="text-center mb-12"
        >
          <h3 className="text-2xl md:text-3xl font-bold mb-4 text-gray-800">
            Why Our Process Justifies Our Pricing
          </h3>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Every step in our process is designed to maximize value and minimize risk for your investment.
          </p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {valuePropositions.map((value, index) => (
            <motion.div
              key={index}
              variants={valueCardVariants}
              className="text-center p-6 bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300"
              whileHover={{ y: -5 }}
            >
              <div className={`w-12 h-12 rounded-xl ${value.color} flex items-center justify-center mx-auto mb-4`}>
                <span className="text-xl">{value.icon}</span>
              </div>
              
              <h4 className="text-lg font-semibold text-gray-800 mb-3">
                {value.title}
              </h4>
              
              <p className="text-gray-600 text-sm leading-relaxed">
                {value.description}
              </p>
            </motion.div>
          ))}
        </motion.div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="text-center mt-16"
        >
          <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl p-8 text-white">
            <h3 className="text-2xl font-bold mb-4">
              Ready to Experience Our Process?
            </h3>
            <p className="text-lg mb-6 opacity-90">
              Let's discuss your project and show you how our systematic approach can deliver exceptional results for your business.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                onClick={() => window.location.href = '/contact'}
                className="bg-white text-indigo-600 px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-all duration-300"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Start Your Project
              </motion.button>
              <motion.button
                onClick={() => window.location.href = '/process'}
                className="border-2 border-white text-white px-8 py-3 rounded-full font-semibold hover:bg-white hover:text-indigo-600 transition-all duration-300"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Learn More About Our Process
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Process;
